import LogoutIcon from '@mui/icons-material/Logout';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useState } from 'react';
import { useGetOne, useNotify, useTranslate, useUpdate } from 'react-admin';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { composeUri } from '@/utils/compose';
import { AVATARS, GROUP_USERS } from '@/utils/CONST';
import RemoveFromAgencyDialog from '@/components/agencies/users/remove-from-agency-dialog';

interface Props {
    clientId: string;
}

const AgencyClientsActionField = ({ clientId }: Props) => {
    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const queryClient = useQueryClient();
    const [update, { isLoading: isLoadingUpdate }] = useUpdate();
    const { agencyId } = useParams();

    // Obtener los datos completos del sitio para acceder a sus groupAvatars
    const { data: siteData } = useGetOne(AVATARS, { id: clientId }, { enabled: !!clientId });

    const handleRemoveFromAgency = () => {
        if (!siteData?.groupAvatars || siteData.groupAvatars.length === 0) {
            notify('errorMessages.no_group_avatars', { type: 'warning' });
            return;
        }

        update(
            GROUP_USERS,
            {
                id: `${composeUri(GROUP_USERS, agencyId)}/add_remove_group_avatars`,
                data: {
                    groupAvatars: siteData.groupAvatars,
                    add: false,
                },
                meta: {
                    api: 'v2',
                },
            },
            {
                onSuccess: () => {
                    notify('agency.site_removed', {
                        type: 'success',
                        messageArgs: { smart_count: 1 },
                    });
                    setOpenRemoveModal(false);
                    queryClient.invalidateQueries({ queryKey: [AVATARS] });
                    queryClient.invalidateQueries({ queryKey: [GROUP_USERS] });
                },
                onError: () => {
                    notify('errorMessages.fail_operation', { type: 'error' });
                },
            },
        );
    };

    return (
        <Box display={'flex'} justifyContent={'end'} width={'100%'}>
            <Tooltip title={translate('agency.remove_site')}>
                <IconButton disabled={isLoadingUpdate} onClick={() => setOpenRemoveModal(true)} color={'primary'}>
                    <LogoutIcon />
                </IconButton>
            </Tooltip>

            {openRemoveModal && (
                <RemoveFromAgencyDialog
                    isLoading={isLoadingUpdate}
                    onClose={() => setOpenRemoveModal(false)}
                    onClick={handleRemoveFromAgency}
                />
            )}
        </Box>
    );
};

export default AgencyClientsActionField;
